/*
 * Churn & Exit (3.8) in prototype mode: the sample in mock/retention.js
 * (churn cases, exits, former customers and win-backs), mock/customers.js and
 * the reason codes of mock/masters.js, served in the shapes the real API
 * gives — ChurnController, ExitController, WinBackController,
 * ChurnReportController and Customer 360's sections (Retention360). The rules
 * (playbooks, the exit checklist, the score, management intervention, after
 * an exit) are Settings → Customer Success Rules' (mock/api/success.js).
 * Writes change the sample in memory until the page is reloaded; win-back
 * opportunities run in the sales stand-in's pipeline (mock/api/sales.js).
 *
 * The customer and sales stand-ins import this file in turn, so their
 * functions are only called from handlers, never while the files load.
 */
import { ok, created, fail, invalid, notFound, paginate, matches } from "./util";
import { churnRisks, exits as sampleExits, exitHistory, formerCustomers, winBackOpportunities } from "@/mock/retention";
import { customers as sampleCustomers } from "@/mock/customers";
import { contacts as sampleContacts } from "@/mock/contacts";
import { staff, products, segments, reasonCodes, reasonCategories, competitors, pipelineStages } from "@/mock/masters";
import { successRules } from "./success";
import { lifecycleOf, moveCustomerLifecycle } from "./customers";
import { addWinBackOpportunity, winBackRecords } from "./sales";

// ---- Shared bits ------------------------------------------------------------------
const DAY = 86400000;
const now = () => new Date().toISOString();
const today = () => now().slice(0, 10);
const plusDays = (n, from = Date.now()) => new Date(new Date(from).getTime() + n * DAY).toISOString().slice(0, 10);
const at = (day, time = "10:00") => (day ? new Date(`${String(day).slice(0, 10)}T${time}:00`).toISOString() : null);
const startOfDay = (d) => new Date(new Date(d).toDateString()).getTime();
const daysTo = (d) => (d ? Math.round((startOfDay(d) - startOfDay(Date.now())) / DAY) : null);
const sameId = (a, b) => a !== null && a !== undefined && b !== null && b !== undefined && String(a) === String(b);
const round = (v) => Math.round(Number(v || 0) * 100) / 100;
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, Math.round(v)));
const sum = (list, f) => round(list.reduce((s, x) => s + Number(f(x) || 0), 0));
const blank = (v) => v === null || v === undefined || String(v).trim() === "";
const ME = "Jane Wanjiku"; // the prototype's signed-in person
let seq = 1;
const uid = (prefix) => `${prefix}-${seq++}`;
const rules = () => successRules();

const FLAGS = { normal: { name: "Normal", tone: "success" }, at_risk: { name: "At risk", tone: "warning" }, critical: { name: "Critical", tone: "danger" } };
const FLAG_ORDER = { normal: 0, at_risk: 1, critical: 2 };
const LEVELS = { low: "Low", medium: "Medium", high: "High", critical: "Critical" };
const STATUSES = { open: "Open", recovered: "Recovered", churned: "Churned", closed: "Closed" };
const SOURCES = { manual: "Flagged by hand", rules: "Churn rules", renewal: "Not renewing", exit: "Exit requested" };
const ACTION_STATUSES = { open: "Open", in_progress: "In progress", done: "Done", cancelled: "Cancelled" };
const SAVE_OUTCOMES = { accepted: { name: "Accepted", tone: "success" }, considering: { name: "Considering", tone: "warning" }, declined: { name: "Declined", tone: "danger" } };
const SAVE_KINDS = { discount: "Discount", service_credit: "Service credit", package_change: "Package change", support: "Extra support", executive: "Executive involvement", training: "Training", other: "Other" };
const STAGES = {
  requested: { name: "Requested", kind: "open", order: 1 }, review: { name: "Account review", kind: "open", order: 2 }, approved: { name: "Approved", kind: "open", order: 3 },
  checklist: { name: "Checklist", kind: "open", order: 4 }, deactivated: { name: "Deactivated", kind: "open", order: 5 }, confirmed: { name: "Confirmed", kind: "won", order: 6 },
  withdrawn: { name: "Withdrawn", kind: "lost", order: 99 },
};
const OPEN = ["requested", "review", "approved", "checklist", "deactivated"];
const CHANNELS = { letter: "Letter", email: "Email", meeting: "Meeting", phone: "Phone", portal: "Portal" };
const RECOVERY_KINDS = { saved: "Saved", withdrawn: "Exit withdrawn", won_back: "Won back" };
const OWNERS = { csm: "The CSM", account_manager: "The account manager", finance: "Finance", support: "Support", implementation: "Implementation", management: "Management", "customer-success": "Customer Success", "sales-manager": "Sales manager" };
const CHURN_PARTS = {
  health: "Health (100 less the health score)", triggers: "Churn triggers seen in 90 days", renewal: "The renewal (not renewing, at risk, near and not in discussion)",
  support: "Support (breaching and critical tickets, open complaints)",
};
const ACTIVE = ["onboarding", "live", "at_risk", "exiting"];
// The churn triggers (Settings → Lookups → Churn triggers) and the churn rule that raises each.
const TRIGGERS = [
  { value: "usage_drop", label: "Usage drop", icon: "iconoir-graph-down", rule: "usage" }, { value: "no_engagement", label: "No engagement", icon: "iconoir-chat-bubble", rule: "engagement" },
  { value: "late_payment", label: "Late payment", icon: "iconoir-wallet", rule: "payment" }, { value: "open_complaint", label: "Open complaint", icon: "iconoir-warning-triangle", rule: "complaint" },
  { value: "poor_satisfaction", label: "Poor satisfaction", icon: "iconoir-emoji-sad", rule: null }, { value: "contract_expiry", label: "Contract expiry", icon: "iconoir-calendar", rule: "expiry" },
  { value: "competitor_approach", label: "Competitor approach", icon: "iconoir-shield", rule: "competitor" }, { value: "sponsor_left", label: "Sponsor left", icon: "iconoir-user-xmark", rule: null },
];
const SAMPLE_TRIGGER = { usage: "usage_drop", engagement: "no_engagement", payment: "late_payment", complaint: "open_complaint", expiry: "contract_expiry", competitor: "competitor_approach" };
const triggerOf = (key) => TRIGGERS.find((t) => t.value === key) ?? { value: key, label: key, icon: "iconoir-warning-circle", rule: null };
const ruleOf = (t) => rules().churn_rules.find((r) => r.key === t.rule && r.on) ?? null;

const staffRow = (id) => {
  const s = staff.find((x) => sameId(x.id, id));
  return s ? { id: s.id, name: s.name } : null;
};
const customerOf = (id) => sampleCustomers.find((c) => sameId(c.id, id)) ?? null;
const lifecycle = (c) => (c ? lifecycleOf(c.id) ?? c.lifecycle : null);
const segmentName = (code) => segments.find((s) => s.code === code)?.name ?? code;
const productShort = (id) => products.find((p) => sameId(p.id, id))?.short ?? "—";
const contactsOf = (customerId) => sampleContacts.filter((k) => sameId(k.customer_id, customerId));
const reasonOf = (code) => reasonCodes.find((r) => r.code === code) ?? null;
const reasonRef = (code) => {
  const r = reasonOf(code);
  return { code, name: r?.name ?? null, category: reasonCategories.find((c) => c.key === r?.category)?.name ?? null, category_key: r?.category ?? null, preventable: !!r?.preventable, recyclable: !!r?.recyclable };
};
const competitorRef = (v) => {
  const c = competitors.find((x) => sameId(x.id, v) || x.name === v);
  return c ? { id: c.id, name: c.name } : null;
};

// ---- The rules from Settings → Customer Success Rules -------------------------------
const closedRisks = () => riskStore.filter((r) => ["recovered", "churned"].includes(r.status));
const playbookRates = () => {
  const out = {};
  closedRisks().filter((r) => r.playbook).forEach((r) => {
    out[r.playbook] ??= { total: 0, kept: 0 };
    out[r.playbook].total++;
    if (r.status === "recovered") out[r.playbook].kept++;
  });
  return Object.fromEntries(Object.entries(out).map(([k, v]) => [k, Math.round((100 * v.kept) / v.total)]));
};
const playbooks = () => {
  const rates = playbookRates();
  return rules().retention_playbooks.map((p) => ({ ...structuredClone(p), success_rate: rates[p.key] ?? null, owner_label: OWNERS[p.owner ?? "csm"] ?? p.owner }));
};
const playbookOf = (key) => playbooks().find((p) => p.key === key) ?? null;

// The rule-based score (ChurnScorer): each part 0–100 risk, weighted; the level from the bands.
const HEALTH_RISK = { healthy: 15, needs_attention: 45, at_risk: 65, critical: 85 };
const HEALTH_LABEL = { healthy: "Healthy", needs_attention: "Needs attention", at_risk: "At risk", critical: "Critical" };
const openRiskOf = (customerId) => riskStore.find((r) => sameId(r.customer_id, customerId) && r.status === "open") ?? null;
const levelOf = (score) => {
  const b = rules().churn_scoring.bands;
  return score === null ? null : score >= b.critical ? "critical" : score >= b.high ? "high" : score >= b.medium ? "medium" : "low";
};
const scoreOf = (customerId) => {
  const c = customerOf(customerId);
  const open = openRiskOf(customerId);
  const seen = open ? open.triggers.filter((t) => !t.cleared_at).length : 0;
  const days = daysTo(c?.renewal_date);
  const complaint = open?.triggers.some((t) => t.trigger === "open_complaint");
  const raw = {
    health: c?.health ? [HEALTH_RISK[c.health], `Health ${HEALTH_LABEL[c.health]} (${100 - HEALTH_RISK[c.health]})`] : [null, "No health score yet"],
    triggers: [Math.min(100, seen * 25), seen ? `${seen} trigger(s) in 90 days` : "No trigger in 90 days"],
    renewal: lifecycle(c) === "exiting" ? [100, "Not renewing"] : days === null ? [null, "No renewal in force"] : [days <= 60 ? 70 : 20, `Renewal in ${days} days`],
    support: complaint ? [60, "An open complaint"] : [20, "No breaching tickets or open complaints"],
  };
  const weights = rules().churn_scoring.weights;
  let total = 0;
  let known = 0;
  const parts = Object.fromEntries(Object.entries(CHURN_PARTS).map(([key, name]) => {
    const weight = Number(weights[key] ?? 0);
    const [risk, signal] = raw[key];
    if (risk !== null && weight > 0) {
      total += risk * weight;
      known += weight;
    }
    return [key, { key, name, weight, risk, signal }];
  }));
  const score = known ? Math.round(total / known) : null;
  return { score, level: levelOf(score), probability: score === null ? null : clamp(score * 0.85, 2, 95), parts, weights: { ...weights }, known_weight: known, heuristic: true };
};
const recoveryOf = (r) => {
  const rate = r.playbook ? playbookRates()[r.playbook] ?? null : null;
  const steps = r.actions.filter((a) => a.status !== "cancelled").length + r.saves.length;
  const progress = steps ? (100 * (r.actions.filter((a) => a.status === "done").length + r.saves.filter((s) => s.outcome === "accepted").length)) / steps : 30;
  const p = 0.5 * (100 - (scoreOf(r.customer_id).probability ?? 50)) + 0.3 * (rate ?? 50) + 0.2 * progress;
  return { probability: clamp(p, 5, 95), playbook_rate: rate, progress: Math.round(progress), heuristic: true };
};

// ---- Churn cases --------------------------------------------------------------------
const ACTION_STATUS = { Done: "done", "In progress": "in_progress", Open: "open", "Pending approval": "in_progress" };
const saveKind = (offer) => (/discount|price/i.test(offer) ? "discount" : /credit/i.test(offer) ? "service_credit" : /package/i.test(offer) ? "package_change" : /specialist|contact|support/i.test(offer) ? "support" : "other");
const riskStore = churnRisks.map((r) => ({
  id: r.id, number: `CHR-${String(r.id).padStart(4, "0")}`, customer_id: r.customer_id, flag: r.flag, status: "open", source: r.triggers.length > 1 ? "rules" : "manual",
  reason: null, owner_id: r.owner_id, playbook: r.playbook, arr: customerOf(r.customer_id)?.arr ?? 0,
  triggers: r.triggers.map((t) => ({ id: uid("trg"), trigger: SAMPLE_TRIGGER[t.key] ?? t.key, detail: t.detail, raises: r.flag === "critical" ? "critical" : "at_risk", source: "rules", detected_at: at(t.at), cleared_at: null })),
  escalated_at: r.escalation ? at(r.escalation.at) : null, escalation_note: r.escalation ? `${r.escalation.note}; told ${r.escalation.to}` : null,
  flagged_at: at([...r.triggers].sort((a, b) => a.at.localeCompare(b.at))[0]?.at), closed_at: null, close_note: null, reason_code: null,
  actions: r.plan.map((p, i) => ({ id: uid("act"), action: p.action, description: null, owner_id: p.owner_id, due_on: p.due, status: ACTION_STATUS[p.status] ?? "open", playbook: r.playbook, step: i + 1, source: "playbook", done_at: p.status === "Done" ? at(p.due) : null })),
  saves: r.saves.map((s) => ({ id: uid("sav"), attempted_on: s.date, by_id: s.by, offer: s.offer, kind: saveKind(s.offer), value_offered: null, outcome: s.outcome.startsWith("Declined") ? "declined" : s.outcome.toLowerCase(), note: s.outcome.split("— ")[1] ?? null, churn_risk_id: r.id, customer_exit_id: null })),
}));
// Cases closed in the last year: what the save rate and the playbooks' records are worked out from.
[[9, "recovered", "Usage back above 80% after retraining", -200, "adoption"], [13, "recovered", "Billing dispute settled with a credit note", -150, "collections"], [2, "recovered", "Executive call and a named support contact", -95, "recovery"],
  [10, "recovered", "Unused modules switched on; usage recovered", -60, "adoption"], [14, "churned", "Moved to SageHR bundled with their PMS", -100, "commercial"], [17, "churned", "Practice closed", -150, null]]
  .forEach(([customerId, status, note, days, playbook]) => {
    const id = riskStore.length + 1;
    riskStore.push({
      id, number: `CHR-${String(id).padStart(4, "0")}`, customer_id: customerId, flag: status === "churned" ? "critical" : "at_risk", status, source: "rules", reason: null,
      owner_id: customerOf(customerId)?.csm_id, playbook, arr: customerOf(customerId)?.arr || 300000, triggers: [], escalated_at: null, escalation_note: null,
      flagged_at: at(plusDays(days - 30)), closed_at: at(plusDays(days)), close_note: note, reason_code: null, actions: [], saves: [],
    });
  });
const recoveryStore = closedRisks().filter((r) => r.status === "recovered").map((r) => ({
  id: uid("rec"), kind: "saved", customer_id: r.customer_id, how: r.close_note, value_saved: r.arr, playbook: r.playbook, recovered_on: r.closed_at.slice(0, 10), risk_id: r.id, exit_id: null, opportunity: null,
}));
const findRisk = (id) => riskStore.find((r) => sameId(r.id, id) || r.number === String(id).toUpperCase()) ?? null;

const customerRef = (c, extra = {}) => (c ? { id: c.id, code: c.code, name: c.name, lifecycle: lifecycle(c), segment: String(c.segment).toLowerCase(), industry: c.industry, ...extra } : null);
const actionRow = (a) => ({
  id: a.id, action: a.action, description: a.description, owner: staffRow(a.owner_id), due_on: a.due_on, overdue: ["open", "in_progress"].includes(a.status) && daysTo(a.due_on) < 0,
  status: a.status, status_label: ACTION_STATUSES[a.status], playbook: a.playbook, step: a.step, source: a.source, task: null, done_at: a.done_at,
});
const saveRow = (s) => ({
  id: s.id, attempted_on: s.attempted_on, by: staffRow(s.by_id), offer: s.offer, kind: s.kind, kind_label: SAVE_KINDS[s.kind] ?? s.kind, value_offered: s.value_offered,
  outcome: s.outcome, outcome_label: SAVE_OUTCOMES[s.outcome]?.name ?? s.outcome, outcome_tone: SAVE_OUTCOMES[s.outcome]?.tone ?? "secondary", note: s.note,
  churn_risk_id: s.churn_risk_id, customer_exit_id: s.customer_exit_id,
});
const riskRow = (r) => {
  const c = customerOf(r.customer_id);
  const s = scoreOf(r.customer_id);
  return {
    id: r.id, number: r.number,
    customer: customerRef(c, { health: c?.health, health_score: c?.health ? 100 - HEALTH_RISK[c.health] : null }),
    flag: r.flag, flag_label: FLAGS[r.flag].name, flag_tone: FLAGS[r.flag].tone, status: r.status, status_label: STATUSES[r.status], is_open: r.status === "open",
    source: r.source, source_label: SOURCES[r.source], reason: r.reason, owner: staffRow(r.owner_id), playbook: r.playbook,
    score: s.score, level: s.level, level_label: LEVELS[s.level] ?? null, probability: s.probability, heuristic: true,
    arr: r.arr, revenue_at_risk: s.probability === null ? null : round((r.arr * s.probability) / 100), masked: false,
    triggers: r.triggers.map((t) => ({ ...t, label: triggerOf(t.trigger).label, icon: triggerOf(t.trigger).icon, rule_key: t.source === "rules" ? triggerOf(t.trigger).rule : null, evidence: null })),
    escalated_at: r.escalated_at, escalation_note: r.escalation_note, flagged_at: r.flagged_at, scored_at: now(), closed_at: r.closed_at, close_note: r.close_note, reason_code: r.reason_code,
    actions_open: r.actions.filter((a) => ["open", "in_progress"].includes(a.status)).length, actions_total: r.actions.filter((a) => a.status !== "cancelled").length, saves_count: r.saves.length,
  };
};
const riskDetail = (r) => {
  const open = r.status === "open";
  const exit = exitStore.find((e) => sameId(e.churn_risk_id, r.id)) ?? null;
  return {
    ...riskRow(r), score_breakdown: scoreOf(r.customer_id).parts, recovery: recoveryOf(r), playbook_detail: playbookOf(r.playbook),
    actions: r.actions.map(actionRow), saves: r.saves.map(saveRow), exit: exit ? { id: exit.id, number: exit.number, stage: exit.stage } : null,
    can: { flag: open, plan: open, escalate: open && !r.escalated_at, close: open, exit: open },
  };
};
const riskList = (q) => riskStore.filter((r) => {
  const status = q.status || "all";
  if (status !== "all" && r.status !== status) return false;
  if (q.flag && r.flag !== q.flag) return false;
  if (q.level && scoreOf(r.customer_id).level !== q.level) return false;
  if (q.owner_id && !sameId(r.owner_id, q.owner_id)) return false;
  if (q.customer_id && !sameId(r.customer_id, q.customer_id)) return false;
  return matches(q.search, r.number, customerOf(r.customer_id)?.name);
}).sort((a, b) => Number(b.status === "open") - Number(a.status === "open") || FLAG_ORDER[b.flag] - FLAG_ORDER[a.flag] || (scoreOf(b.customer_id).score ?? 0) - (scoreOf(a.customer_id).score ?? 0));
const saveRate = () => {
  const closed = closedRisks().filter((r) => daysTo(r.closed_at) >= -365);
  const kept = closed.filter((r) => r.status === "recovered").length;
  return { saved: kept, closed: closed.length, rate: closed.length ? Math.round((100 * kept) / closed.length) : null, value_saved: sum(recoveryStore.filter((x) => daysTo(x.recovered_on) >= -365), (x) => x.value_saved) };
};
const resourcePage = (items, q) => {
  const { data, ...p } = paginate(items, q, Number(q.per_page) || 50);
  return { data, links: { first: "?page=1", last: `?page=${p.last_page}`, prev: null, next: null }, meta: { current_page: p.current_page, from: p.from, last_page: p.last_page, per_page: p.per_page, to: p.to, total: p.total } };
};

// The plan: a playbook's steps (each once), an action, escalating to management.
const startPlaybook = (r, key) => {
  const p = playbookOf(key);
  if (!p) return false;
  r.playbook = p.key;
  p.steps.forEach((st, i) => {
    if (r.actions.some((a) => a.playbook === p.key && a.step === i + 1)) return;
    const c = customerOf(r.customer_id);
    const owner = { account_manager: c?.account_manager_id, finance: 11, support: 8, implementation: 6, management: 2 }[st.owner ?? p.owner] ?? r.owner_id;
    r.actions.push({ id: uid("act"), action: st.action, description: null, owner_id: owner, due_on: plusDays(Number(st.days) || 0), status: "open", playbook: p.key, step: i + 1, source: "playbook", done_at: null });
  });
  return true;
};
const escalate = (r, note = null) => {
  r.escalated_at = now();
  r.escalation_note = note || "High-value account at high risk; told Brian Otieno (Head of Sales) and Grace Njeri (Head of CS)";
};
const needsManagement = (r) => {
  const m = rules().churn_management;
  return r.arr >= Number(m.threshold ?? 0) && (m.levels ?? []).includes(scoreOf(r.customer_id).level);
};
const flagCustomer = (c, body) => {
  const id = Math.max(...riskStore.map((r) => Number(r.id) || 0)) + 1;
  const r = {
    id, number: `CHR-${String(id).padStart(4, "0")}`, customer_id: c.id, flag: body.flag, status: "open", source: body.source ?? "manual", reason: body.reason ?? null, owner_id: c.csm_id ?? c.account_manager_id,
    playbook: null, arr: c.arr, triggers: [], escalated_at: null, escalation_note: null, flagged_at: now(), closed_at: null, close_note: null, reason_code: null, actions: [], saves: [],
  };
  if (body.trigger) r.triggers.push({ id: uid("trg"), trigger: body.trigger, detail: body.detail || null, raises: body.flag === "normal" ? null : body.flag, source: "manual", detected_at: now(), cleared_at: null });
  riskStore.push(r);
  const auto = playbooks().find((p) => p.auto && p.for.includes(body.trigger));
  if (auto) startPlaybook(r, auto.key);
  if (rules().churn_management.auto_escalate && needsManagement(r)) escalate(r);
  if (body.flag !== "normal" && lifecycle(c) === "live") moveCustomerLifecycle(c.id, "at_risk", "churn_flag", `Flagged ${FLAGS[body.flag].name.toLowerCase()}`);
  return r;
};
const findAction = (id) => {
  for (const r of riskStore) {
    const a = r.actions.find((x) => sameId(x.id, id));
    if (a) return { r, a };
  }
  return null;
};
const findSave = (id) => {
  for (const r of riskStore) {
    const s = r.saves.find((x) => sameId(x.id, id));
    if (s) return { r, s };
  }
  return null;
};
const closeRisk = (r, status, note) => {
  Object.assign(r, { status, closed_at: now(), close_note: note });
  r.actions.filter((a) => ["open", "in_progress"].includes(a.status)).forEach((a) => (a.status = "cancelled"));
};
const addSave = (list, body, link) => {
  if (blank(body.offer)) return invalid({ offer: ["Say what was offered."] }, "Say what was offered.");
  list.push({ id: uid("sav"), attempted_on: body.attempted_on || today(), by_id: body.by_id ?? 1, offer: body.offer, kind: body.kind || saveKind(body.offer), value_offered: body.value_offered ?? null,
    outcome: body.outcome || "considering", note: body.note || null, churn_risk_id: null, customer_exit_id: null, ...link });
  return null;
};

// ---- Exits ----------------------------------------------------------------------------
const APPROVERS = [["Customer Success Manager", "Samuel Kariuki"], ["Finance", "Mercy Wambui"], ["Head of Customer Success", "Grace Njeri"]];
const ownerKey = (key) => rules().exit_checklist.find((x) => x.key === key)?.owner ?? "csm";
const ownerStaff = (key, c) => ({ csm: c?.csm_id, account_manager: c?.account_manager_id, finance: 11, support: 8, implementation: 6, management: 2 })[key] ?? null;
const affectedOf = (c, value) => (c?.product_ids ?? []).map((pid, i) => ({
  product_id: pid, product: productShort(pid), package: null, licences: c.users?.licensed ?? null, monthly: i === 0 ? round(value / 12) : 0, annual: i === 0 ? value : 0, contract: null, status: "active",
}));
const auditItem = (title, body = null, extra = {}) => ({ kind: "note", id: uid("aud"), at: now(), title, body, by: ME, tone: "primary", icon: "iconoir-arrow-right", meta: {}, ...extra });
const exitStore = sampleExits.map((e) => {
  const c = customerOf(e.customer_id);
  const chain = e.approvals.map((a) => ({ sequence: a.level, step: a.role, approver: a.approver, action: a.status, comments: a.comment || null, acted_at: at(a.at), due_at: null }));
  const done = (x) => (x.done ? at(x.done_on, "14:00") : null);
  const row = {
    id: e.id, number: e.number, customer_id: e.customer_id, stage: e.stage, requested_on: e.requested_on, exit_date: e.exit_date, final_date: e.stage === "confirmed" ? e.exit_date : null,
    requested_by_name: e.requested_by, contact_id: contactsOf(e.customer_id)[0]?.id ?? null, channel: /letter/i.test(e.requested_by) ? "letter" : /email/i.test(e.requested_by) ? "email" : "meeting",
    reason_code: e.reason_code, competitor_id: competitorRef(e.competitor)?.id ?? null, note: e.note, value: e.value, owner_id: e.owner_id,
    approval_status: e.stage === "requested" ? null : chain.every((a) => a.action === "approved") ? "approved" : "pending", chain: e.stage === "requested" ? [] : chain, decision_note: null,
    reference: `APR-${String(400 + e.id).padStart(4, "0")}`,
    checklist: e.checklist.map((x) => ({ id: uid("chk"), key: x.key, area: x.area, item: x.item, owner_key: ownerKey(x.key), owner_id: x.owner_id, required: true, done_at: done(x), done_by: x.done ? staffRow(x.owner_id)?.name : null, note: null })),
    invoices: e.review.invoices.map((i) => ({ id: uid("inv"), number: i.no, amount: i.amount, due_on: i.due, status: i.status === "Paid" ? "paid" : "open", note: null })),
    obligations: e.review.obligations, contract_terms: e.review.contract_terms, review_started_at: e.stage === "requested" ? null : at(e.requested_on, "15:00"),
    affected: affectedOf(c, e.value), deactivation: null, deactivated_at: null, confirmed_at: e.stage === "confirmed" ? at(e.exit_date, "16:00") : null, outstanding_issues: null,
    letter: /letter/i.test(e.requested_by) ? { id: uid("att"), name: `${e.number} exit letter.pdf` } : null,
    survey: {
      invitation: e.survey ? { id: uid("sur"), status: "responded", channel: "email", contact: c?.primary_contact ?? null, send_at: at(e.exit_date), sent_at: at(e.exit_date), survey_id: 1 } : null,
      response: e.survey ? { id: uid("rsp"), score: e.survey.nps, score_kind: "nps", answers: [{ question: "How satisfied were you with us overall? (1–5)", answer: e.survey.satisfaction }, { question: "How likely are you to recommend us? (0–10)", answer: e.survey.nps }, { question: "What could we have done better?", answer: e.survey.comments }], submitted_at: at(plusDays(3, e.exit_date)) } : null,
    },
    saves: [], churn_risk_id: riskStore.find((r) => sameId(r.customer_id, e.customer_id) && r.status === "open")?.id ?? null,
    reengage_on: e.reengage_on, winback_opportunity_id: null, withdrawn_at: null, withdraw_reason: null, created_at: at(e.requested_on, "09:00"),
    audit: [
      { kind: "stage", id: uid("aud"), at: at(e.requested_on, "09:00"), title: "Exit requested", body: `${reasonOf(e.reason_code)?.name} — ${e.note}`, by: staffRow(e.owner_id)?.name, tone: "primary", icon: "iconoir-log-out", meta: {} },
      ...chain.filter((a) => a.acted_at).map((a) => ({ kind: "approval", id: uid("aud"), at: a.acted_at, title: `${a.step}: ${a.action === "approved" ? "Approved" : "Rejected"}`, body: a.comments, by: a.approver, tone: "success", icon: "iconoir-check-circle", meta: {} })),
      ...e.checklist.filter((x) => x.done).map((x) => ({ kind: "task", id: uid("aud"), at: done(x), title: `Checklist: ${x.item}`, body: null, by: staffRow(x.owner_id)?.name, tone: "success", icon: "iconoir-check", meta: {} })),
    ],
  };
  if (["deactivated", "confirmed"].includes(e.stage)) {
    row.deactivation = { contracts: [], subscriptions: 1, product_accounts: (c?.product_accounts ?? []).map((a) => a.tenant_code ?? a.address), connections: [], renewals: [], users: `${c?.users?.licensed ?? "The"} customer user(s) lose access with the product accounts closed and the connections revoked.` };
    row.deactivated_at = at(e.exit_date, "12:00");
  }
  return row;
});
const findExit = (id) => exitStore.find((e) => sameId(e.id, id) || e.number === String(id).toUpperCase()) ?? null;
const isOpen = (e) => OPEN.includes(e.stage);
const outstanding = (e) => sum(e.invoices.filter((i) => i.status === "open"), (i) => i.amount);
const waitingOf = (e) => (e.approval_status === "pending" ? e.chain.find((a) => a.action === "pending") ?? null : null);
const winBackChance = (e) => {
  const r = reasonOf(e.reason_code);
  if (!r?.recyclable) return { probability: 5, heuristic: true, why: [`${e.reason_code} rules out a return`] };
  const why = [];
  let p = 40;
  const months = Math.floor(-(daysTo(e.final_date ?? e.exit_date) ?? 0) / 30);
  if (r.preventable) (p += 10), why.push("The reason was in our hands (+10)");
  if (months < 3) (p -= 15), why.push("Left under 3 months ago (−15)");
  else if (months <= 12) (p += 10), why.push(`Left ${months} months ago (+10)`);
  if (e.competitor_id && months >= 9) (p += 5), why.push("The competitor's first term is coming round (+5)");
  const nps = e.survey.response?.score;
  if (nps !== undefined && nps !== null && nps >= 7) (p += 10), why.push(`Exit survey NPS ${nps} (+10)`);
  return { probability: clamp(p, 5, 90), heuristic: true, why };
};
const exitRow = (e) => {
  const c = customerOf(e.customer_id);
  const waiting = waitingOf(e);
  const opp = e.winback_opportunity_id ? pipelineOpps().find((o) => sameId(o.id, e.winback_opportunity_id)) : null;
  const contact = contactsOf(e.customer_id).find((k) => sameId(k.id, e.contact_id));
  return {
    id: e.id, number: e.number, customer: customerRef(c), stage: e.stage, stage_label: STAGES[e.stage].name, is_open: isOpen(e), requested_on: e.requested_on, exit_date: e.exit_date,
    final_date: e.final_date, requested_by_name: e.requested_by_name, contact: contact ? { id: contact.id, name: contact.name, title: contact.title } : null, channel: e.channel,
    reason_code: e.reason_code, reason: reasonRef(e.reason_code), competitor: competitorRef(e.competitor_id), note: e.note, value: e.value, currency: "KES", masked: false,
    owner: staffRow(e.owner_id), approval_status: e.approval_status, waiting_on: waiting ? { step: waiting.step, approver: waiting.approver } : null,
    checklist_done: e.checklist.filter((x) => x.done_at).length, checklist_total: e.checklist.length, outstanding: outstanding(e), reengage_on: e.reengage_on,
    winback_opportunity: opp ? { id: opp.id, code: opp.code, stage: opp.stage } : null, withdrawn_at: e.withdrawn_at, withdraw_reason: e.withdraw_reason, created_at: e.created_at,
  };
};
const exitDetail = (e) => {
  const c = customerOf(e.customer_id);
  const stage = e.stage;
  const waiting = waitingOf(e);
  const given = Math.round((startOfDay(e.exit_date) - startOfDay(e.requested_on)) / DAY);
  const risk = riskStore.find((r) => sameId(r.id, e.churn_risk_id));
  const due = e.final_date ?? e.exit_date;
  const recyclable = reasonOf(e.reason_code)?.recyclable;
  return {
    ...exitRow(e),
    affected: structuredClone(e.affected),
    review: { obligations: e.obligations, contract_terms: e.contract_terms, notice_days: 30, notice_given_days: given, notice_met: given >= 30, contracts: [], started_at: e.review_started_at },
    invoices: e.invoices.map((i) => ({ ...i, display_status: i.status === "open" && i.due_on && daysTo(i.due_on) < 0 ? "overdue" : i.status })),
    approval: { status: e.approval_status, reference: e.chain.length ? e.reference : null, request_id: e.chain.length ? e.id : null, note: e.decision_note, chain: structuredClone(e.chain), waiting_on: waiting?.approver ?? null, mine: !!waiting },
    checklist: e.checklist.map((x) => ({
      id: x.id, key: x.key, area: x.area, item: x.item, owner: staffRow(x.owner_id ?? ownerStaff(x.owner_key, c)), owner_label: OWNERS[x.owner_key] ?? x.owner_key, required: x.required, done: !!x.done_at,
      done_at: x.done_at, done_by: x.done_by, note: x.note, blocked: x.key === "payment" && !x.done_at && outstanding(e) > 0 ? "Waiting for the outstanding invoices to be settled or written off" : null,
    })),
    deactivation: e.deactivation ?? {
      contracts: [`CON-${String(c?.id ?? 0).padStart(4, "0")}`], product_accounts: (c?.product_accounts ?? []).filter((a) => a.status !== "archived").map((a) => `${productShort(a.product_id)} ${a.tenant_code ?? a.address}`),
      connections: [], users: c?.users?.licensed ?? null, preview: true,
    },
    deactivated_at: e.deactivated_at, data_deletion_on: plusDays(Number(rules().win_back.data_deletion_days ?? 90), due), confirmed_at: e.confirmed_at, outstanding_issues: e.outstanding_issues,
    letter: e.letter, survey: structuredClone(e.survey), saves: e.saves.map(saveRow), risk: risk ? { id: risk.id, number: risk.number, flag: risk.flag, status: risk.status } : null,
    win_back: stage === "confirmed" ? winBackChance(e) : null,
    audit: [...e.audit].sort((a, b) => String(b.at).localeCompare(String(a.at))),
    can: {
      edit: isOpen(e), review: stage === "requested", submit: ["requested", "review"].includes(stage) && e.approval_status !== "pending", decide: e.approval_status === "pending",
      tick: ["approved", "checklist"].includes(stage), deactivate: ["approved", "checklist"].includes(stage), confirm: stage === "deactivated",
      withdraw: ["requested", "review", "approved", "checklist"].includes(stage), reclassify: isOpen(e) || stage === "confirmed", win_back: stage === "confirmed" && !!recyclable && !e.winback_opportunity_id,
    },
  };
};
const exitList = (q, { stage = true } = {}) => exitStore.filter((e) => {
  if (stage && q.stage && (q.stage === "open" ? !isOpen(e) : e.stage !== q.stage)) return false;
  if (q.customer_id && !sameId(e.customer_id, q.customer_id)) return false;
  if (q.owner_id && !sameId(e.owner_id, q.owner_id)) return false;
  if (q.preventable !== undefined && q.preventable !== "" && reasonRef(e.reason_code).preventable !== ["1", "true", true, 1].includes(q.preventable)) return false;
  return matches(q.search, e.number, customerOf(e.customer_id)?.name);
}).sort((a, b) => Number(isOpen(b)) - Number(isOpen(a)) || String(b.requested_on).localeCompare(String(a.requested_on)));
const move = (e, stage, title, body = null) => {
  e.audit.push(auditItem(title ?? `Moved from ${STAGES[e.stage].name} to ${STAGES[stage].name}`, body, { kind: "stage" }));
  e.stage = stage;
};
const answer = (e, message) => ok(exitDetail(e), message);
const newExit = (c, body) => {
  const id = Math.max(...exitStore.map((e) => Number(e.id) || 0)) + 1;
  const value = Number(body.value) || c.arr || 0;
  const e = {
    id, number: `EXT-${String(id).padStart(4, "0")}`, customer_id: c.id, stage: "requested", requested_on: body.requested_on || today(), exit_date: body.exit_date || plusDays(30), final_date: null,
    requested_by_name: body.requested_by_name || "The customer", contact_id: body.contact_id || null, channel: body.channel || null, reason_code: String(body.reason_code).toUpperCase(),
    competitor_id: body.competitor_id || null, note: body.note || null, value, owner_id: c.csm_id ?? c.account_manager_id, approval_status: null, chain: [], decision_note: null, reference: `APR-${400 + id}`,
    checklist: [], invoices: [], obligations: null, contract_terms: null, review_started_at: null, affected: affectedOf(c, value), deactivation: null, deactivated_at: null, confirmed_at: null,
    outstanding_issues: null, letter: body.letter?.name ? { id: uid("att"), name: body.letter.name } : null, survey: { invitation: null, response: null }, saves: [],
    churn_risk_id: openRiskOf(c.id)?.id ?? null, reengage_on: null, winback_opportunity_id: null, withdrawn_at: null, withdraw_reason: null, created_at: now(), audit: [],
  };
  e.audit.push(auditItem("Exit requested", `${reasonOf(e.reason_code)?.name ?? e.reason_code}${e.note ? ` — ${e.note}` : ""}`, { kind: "stage", icon: "iconoir-log-out" }));
  exitStore.push(e);
  moveCustomerLifecycle(c.id, "exiting", "exit_requested", `Exit ${e.number}`);
  return e;
};
const exitMissing = (body) => {
  const errors = {};
  if (blank(body.customer_id)) errors.customer_id = ["Choose the customer."];
  if (blank(body.exit_date)) errors.exit_date = ["Give the exit date the customer asked for."];
  if (blank(body.requested_by_name)) errors.requested_by_name = ["Say who asked for the exit, and how."];
  if (blank(body.reason_code)) errors.reason_code = ["Say why the customer is leaving: choose an exit reason."];
  return Object.keys(errors).length ? invalid(errors, Object.values(errors)[0][0]) : null;
};
const findInvoice = (id) => {
  for (const e of exitStore) {
    const i = e.invoices.find((x) => sameId(x.id, id));
    if (i) return { e, i };
  }
  return null;
};
const findItem = (id) => {
  for (const e of exitStore) {
    const x = e.checklist.find((y) => sameId(y.id, id));
    if (x) return { e, x };
  }
  return null;
};
const recoverCustomer = (customerId) => ["at_risk", "exiting"].includes(lifecycleOf(customerId)) && moveCustomerLifecycle(customerId, "live", "recovered", "Kept: the churn case or exit closed as saved");

// ---- Win-back --------------------------------------------------------------------------
// The sample's win-back opportunities join the sales pipeline the first time they are asked for.
const SAMPLE_STAGE = { "Re-engage": "qualified", Conversation: "discovery", Proposal: "proposal", "Won back": "won", "Not now": "lost" };
let seeded = false;
const pipelineOpps = () => {
  if (!seeded) {
    seeded = true;
    winBackOpportunities.forEach((o) => {
      const f = formerCustomers.find((x) => x.id === o.former_id);
      addWinBackOpportunity({ customer_id: f?.customer_id ?? null, organisation: f?.name ?? "Former customer", value: o.value, owner_id: o.owner_id, stage: SAMPLE_STAGE[o.stage] ?? "qualified",
        created_at: at(o.opened), closed_at: o.closed ? at(o.closed) : null, won_on: o.stage === "Won back" ? o.closed : null });
    });
  }
  return winBackRecords();
};
const stageName = (key) => pipelineStages.find((s) => s.key === key)?.name ?? key;
const oppCustomer = (o) => (o.customer_id ? (({ id, code, name }) => ({ id, code, name }))(customerOf(o.customer_id) ?? { id: o.customer_id, code: null, name: o.organisation }) : { id: null, code: null, name: o.organisation });
const lastExit = (customerId) => exitStore.filter((e) => sameId(e.customer_id, customerId) && e.stage === "confirmed").sort((a, b) => String(b.final_date).localeCompare(String(a.final_date)))[0] ?? null;
const exitRef = (e) => (e ? { id: e.id, number: e.number, reason_code: e.reason_code, reason: reasonRef(e.reason_code).name, category: reasonRef(e.reason_code).category, preventable: reasonRef(e.reason_code).preventable,
  recyclable: reasonRef(e.reason_code).recyclable, competitor: competitorRef(e.competitor_id)?.name ?? null, note: e.note } : null);
const winBackOf = (customerId) => pipelineOpps().filter((o) => sameId(o.customer_id, customerId)).sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)))[0] ?? null;
const former = () => sampleCustomers.filter((c) => lifecycle(c) === "former").map((c) => {
  const e = lastExit(c.id);
  const opp = (e?.winback_opportunity_id && pipelineOpps().find((o) => sameId(o.id, e.winback_opportunity_id))) || winBackOf(c.id);
  return {
    customer: { id: c.id, code: c.code, name: c.name, industry: c.industry, segment: String(c.segment).toLowerCase() }, exit: exitRef(e),
    exited_on: e?.final_date ?? c.exited_on ?? null, reengage_on: e?.reengage_on ?? null,
    due: !!(e && reasonRef(e.reason_code).recyclable && e.reengage_on && daysTo(e.reengage_on) <= 0 && !(opp && !opp.closed_at)),
    last_value: e?.value ?? c.arr, probability: e ? winBackChance(e) : { probability: 5, heuristic: true, why: ["No exit on record"] }, owner: staffRow(c.account_manager_id),
    opportunity: opp ? { id: opp.id, code: opp.code, stage: opp.stage, stage_name: stageName(opp.stage), value: opp.value, is_open: !opp.closed_at, won_on: opp.won_on } : null,
  };
}).sort((a, b) => String(a.reengage_on ?? "9999").localeCompare(String(b.reengage_on ?? "9999")));
const wonBack = () => pipelineOpps().filter((o) => o.stage === "won").map((o) => ({
  id: `wb-${o.id}`, kind: "won_back", customer: oppCustomer(o), how: `Won back through ${o.code}`, value: o.value, recovered_on: o.won_on ?? o.closed_at?.slice(0, 10) ?? null, opportunity: { id: o.id, code: o.code },
}));
const createWinBack = (c, body) => {
  const e = lastExit(c.id);
  const opp = addWinBackOpportunity({
    customer_id: c.id, organisation: c.name, segment: String(c.segment).toLowerCase(), value: Number(body.value) || e?.value || c.arr || 0, owner_id: body.owner_id ?? c.account_manager_id,
    expected_close_on: body.expected_close_on, note: body.note ?? `Re-open the conversation with ${c.name}`,
  });
  if (e) {
    e.winback_opportunity_id = opp.id;
    e.audit.push(auditItem(`Win-back ${opp.code} opened`, null, { tone: "success", icon: "iconoir-refresh" }));
  }
  return opp;
};

// ---- Churn analysis (ChurnReports) -------------------------------------------------------
const analysisRows = () => [
  ...exitStore.filter((e) => e.stage === "confirmed").map((e) => {
    const c = customerOf(e.customer_id);
    return { industry: c?.industry ?? "—", segment: segmentName(c?.segment), products: (c?.product_ids ?? []).map(productShort), reason_code: e.reason_code, value: e.value, final_date: e.final_date };
  }),
  ...exitHistory.map((h) => ({ industry: h.industry, segment: segmentName(h.segment), products: [productShort(h.product_id)], reason_code: h.reason_code, value: h.value, final_date: h.exited_on })),
].filter((r) => daysTo(r.final_date) >= -365).map((r) => ({ ...r, ...(({ name, category, preventable }) => ({ reason: name, category, preventable }))(reasonRef(r.reason_code)) }));
const group = (rows, key, many = false) => {
  const groups = {};
  rows.forEach((r) => (many ? key(r) : [key(r)]).forEach((name) => {
    groups[name] ??= { name, count: 0, value: 0, preventable: 0, preventable_value: 0 };
    const g = groups[name];
    g.count++;
    g.value += r.value;
    if (r.preventable) (g.preventable++, (g.preventable_value += r.value));
  }));
  return Object.values(groups).sort((a, b) => b.value - a.value);
};
const winBackFigures = () => {
  const opps = pipelineOpps().filter((o) => daysTo(o.created_at) >= -365);
  const won = opps.filter((o) => o.stage === "won");
  const lost = opps.filter((o) => o.stage === "lost");
  const open = opps.filter((o) => !o.closed_at);
  return { opened: opps.length, open: open.length, won: won.length, lost: lost.length, won_value: sum(won, (o) => o.value), open_value: sum(open, (o) => o.value),
    rate: won.length + lost.length ? Math.round((1000 * won.length) / (won.length + lost.length)) / 10 : null };
};
const recoveredFigures = () => {
  const lines = [...recoveryStore.map((x) => ({ ...x, value: x.value_saved })), ...wonBack()].filter((x) => daysTo(x.recovered_on) >= -365);
  return {
    total: { count: lines.length, value: sum(lines, (x) => x.value) },
    by_kind: Object.entries(RECOVERY_KINDS).map(([kind, lbl]) => ({ kind, label: lbl, count: lines.filter((x) => x.kind === kind).length, value: sum(lines.filter((x) => x.kind === kind), (x) => x.value) })),
    lines: lines.map((x) => ({ id: x.id, kind: x.kind, customer: x.customer ?? (({ id, code, name }) => ({ id, code, name }))(customerOf(x.customer_id) ?? {}), how: x.how, value: x.value, playbook: x.playbook ?? null, recovered_on: x.recovered_on })),
  };
};
const summary = (rows = analysisRows()) => {
  const atStart = sampleCustomers.filter((c) => daysTo(c.customer_since) < -365).length + exitHistory.length;
  const lost = sum(rows, (r) => r.value);
  const arrNow = sum(sampleCustomers.filter((c) => lifecycle(c) !== "former"), (c) => c.arr);
  const preventable = rows.filter((r) => r.preventable);
  const open = exitStore.filter(isOpen);
  return {
    customers_at_start: atStart, churned: rows.length, churn_rate: atStart ? Math.round((1000 * rows.length) / atStart) / 10 : null, arr_lost: lost, arr_now: arrNow,
    revenue_churn: arrNow + lost > 0 ? Math.round((1000 * lost) / (arrNow + lost)) / 10 : null, preventable: preventable.length, preventable_value: sum(preventable, (r) => r.value),
    preventable_share: rows.length ? Math.round((1000 * preventable.length) / rows.length) / 10 : null, in_progress: open.length, in_progress_value: sum(open, (e) => e.value),
    recovered: recoveredFigures().total, win_back: winBackFigures(),
  };
};
const months = () => Array.from({ length: 12 }, (_, i) => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - (11 - i));
  return { month: d.toISOString().slice(0, 7), label: d.toLocaleString("en", { month: "short", year: "numeric" }) };
});
const sections = {
  summary: (rows) => summary(rows),
  reasons: (rows) => ({ categories: group(rows, (r) => r.category ?? "Other"), codes: group(rows, (r) => `${r.reason_code} ${r.reason ?? ""}`.trim()) }),
  products: (rows) => group(rows, (r) => (r.products.length ? r.products : ["—"]), true),
  industries: (rows) => group(rows, (r) => r.industry ?? "—"),
  segments: (rows) => group(rows, (r) => r.segment ?? "—"),
  trend: (rows) => months().map((m) => {
    const inMonth = rows.filter((r) => String(r.final_date).startsWith(m.month));
    return { ...m, count: inMonth.length, value: sum(inMonth, (r) => r.value), preventable: inMonth.filter((r) => r.preventable).length };
  }),
  preventable: (rows) => ({
    preventable: { count: rows.filter((r) => r.preventable).length, value: sum(rows.filter((r) => r.preventable), (r) => r.value) },
    unavoidable: { count: rows.filter((r) => !r.preventable).length, value: sum(rows.filter((r) => !r.preventable), (r) => r.value) },
    by_category: group(rows, (r) => r.category ?? "Other"),
  }),
  recovered: () => recoveredFigures(),
  "win-back": () => winBackFigures(),
  cohorts: () => Object.values(sampleCustomers.reduce((out, c) => {
    const d = new Date(c.customer_since);
    const q = `${d.getFullYear()}-Q${Math.floor(d.getMonth() / 3) + 1}`;
    out[q] ??= { cohort: q, customers: 0, left: 0 };
    out[q].customers++;
    if (lifecycle(c) === "former") out[q].left++;
    return out;
  }, {})).sort((a, b) => a.cohort.localeCompare(b.cohort)).slice(-8).map((g) => ({ ...g, retained: Math.round((1000 * (g.customers - g.left)) / g.customers) / 10 })),
};
const period = () => ({ from: plusDays(-364), to: today() });

// ---- Customer 360's sections (Retention360) ------------------------------------------------
export const retentionSections = (customerId) => {
  const c = customerOf(customerId);
  const lc = lifecycle(c);
  const active = ACTIVE.includes(lc);
  const cases = riskStore.filter((r) => sameId(r.customer_id, customerId)).sort((a, b) => String(b.flagged_at).localeCompare(String(a.flagged_at)));
  const open = cases.find((r) => r.status === "open");
  const exits = exitStore.filter((e) => sameId(e.customer_id, customerId)).sort((a, b) => String(b.requested_on).localeCompare(String(a.requested_on)));
  const openExit = exits.find(isOpen);
  const e = lastExit(customerId);
  const opp = lc === "former" ? winBackOf(customerId) : null;
  return {
    churn: {
      available: true, visible: true, current: open ? riskDetail(open) : null, score: active ? scoreOf(customerId) : null,
      history: cases.filter((r) => r.status !== "open").map((r) => ({ id: r.id, number: r.number, flag: r.flag, status: r.status, status_label: STATUSES[r.status], flagged_at: r.flagged_at, closed_at: r.closed_at, close_note: r.close_note })),
      recoveries: recoveryStore.filter((x) => sameId(x.customer_id, customerId)).map((x) => ({ kind: x.kind, label: RECOVERY_KINDS[x.kind], how: x.how, recovered_on: x.recovered_on })),
      can: { flag: active },
    },
    exit: { available: true, visible: true, current: openExit ? exitRow(openExit) : null, history: exits.filter((x) => x !== openExit).map(exitRow), can: { create: !openExit && active } },
    win_back: lc !== "former" ? { available: true, visible: false, module: "Churn & Exit", former: false } : {
      available: true, visible: true, former: true, exit: e ? { ...exitRef(e), final_date: e.final_date } : null, reengage_on: e?.reengage_on ?? null, probability: e ? winBackChance(e) : null,
      opportunity: opp ? { id: opp.id, code: opp.code, stage: opp.stage, is_open: !opp.closed_at } : null,
      can: { create: (!opp || !!opp.closed_at) && (e ? reasonRef(e.reason_code).recyclable : true) },
    },
  };
};

// ---- Routes -------------------------------------------------------------------------------
const CAN = { flag: true, plan: true, edit: true, exit: true, win_back: true };
const riskAnswer = (r, message) => ok(riskDetail(r), message);
const withRisk = (id, fn) => {
  const r = findRisk(id);
  if (!r) return notFound("Churn risk not found.");
  if (r.status !== "open") return fail(`${r.number} is ${STATUSES[r.status].toLowerCase()}; only an open case changes.`, null, 409);
  return fn(r);
};
const withExit = (id, fn) => {
  const e = findExit(id);
  return e ? fn(e) : notFound("Exit not found.");
};

export default [
  // Churn risk and retention
  ["get", "tenant/churn/meta", () => ok({
    triggers: TRIGGERS.map((t) => {
      const r = ruleOf(t);
      return { label: t.label, icon: t.icon, value: t.value, rule: r ? `${r.condition} ${r.value} ${r.unit}`.trim() : null, rule_key: r?.key ?? null };
    }),
    flags: Object.entries(FLAGS).map(([key, f]) => ({ key, ...f })),
    levels: Object.entries(LEVELS).map(([key, name]) => ({ key, name })),
    statuses: Object.entries(STATUSES).map(([key, name]) => ({ key, name })),
    playbooks: playbooks(),
    scoring: { ...structuredClone(rules().churn_scoring), parts: CHURN_PARTS },
    management: { levels: [...rules().churn_management.levels], threshold: Number(rules().churn_management.threshold) },
    customers: sampleCustomers.filter((c) => ACTIVE.includes(lifecycle(c)) && !openRiskOf(c.id)).sort((a, b) => a.name.localeCompare(b.name)).map((c) => ({ id: c.id, code: c.code, name: c.name, lifecycle: lifecycle(c) })),
    staff: staff.filter((s) => s.status === "active").map((s) => ({ id: s.id, name: s.name })),
    save_kinds: Object.entries(SAVE_KINDS).map(([key, name]) => ({ key, name })),
    save_outcomes: Object.entries(SAVE_OUTCOMES).map(([key, o]) => ({ key, ...o })),
    can: CAN,
  })],
  ["get", "tenant/churn/risks", ({ query }) => {
    const open = riskStore.filter((r) => r.status === "open");
    return ok({
      ...resourcePage(riskList(query).map(riskRow), query),
      counts: Object.entries(FLAGS).map(([key, f]) => ({ key, name: f.name, tone: f.tone, count: open.filter((r) => r.flag === key).length })),
      levels: Object.entries(LEVELS).map(([key, name]) => ({ key, name, count: open.filter((r) => scoreOf(r.customer_id).level === key).length })),
      stats: { open: open.length, revenue_at_risk: sum(open, (r) => riskRow(r).revenue_at_risk), escalated: open.filter((r) => r.escalated_at).length, saves: saveRate(), masked: false },
      can: CAN,
    });
  }],
  ["get", "tenant/churn/recoveries", ({ query }) => {
    const lines = [...recoveryStore.map((x) => ({ id: x.id, kind: x.kind, customer: (({ id, code, name }) => ({ id, code, name }))(customerOf(x.customer_id) ?? {}), how: x.how, value_saved: x.value_saved, playbook: x.playbook,
      recovered_on: x.recovered_on, risk: x.risk_id ? (({ id, number }) => ({ id, number }))(findRisk(x.risk_id)) : null, exit: x.exit_id ? (({ id, number }) => ({ id, number }))(findExit(x.exit_id)) : null, opportunity: x.opportunity })),
      ...wonBack().map((x) => ({ ...x, value_saved: x.value, playbook: null, risk: null, exit: null }))]
      .filter((x) => !query.kind || x.kind === query.kind).map((x) => ({ ...x, kind_label: RECOVERY_KINDS[x.kind] })).sort((a, b) => String(b.recovered_on).localeCompare(String(a.recovered_on)));
    const { data, ...p } = paginate(lines, query, 50);
    return ok({ data, meta: { current_page: p.current_page, last_page: p.last_page, per_page: p.per_page, total: p.total }, masked: false });
  }],
  ["get", "tenant/churn/risks/:id", ({ params }) => (findRisk(params.id) ? ok(riskDetail(findRisk(params.id))) : notFound("Churn risk not found."))],
  ["post", "tenant/churn/risks", ({ body }) => {
    const c = customerOf(body.customer_id);
    if (!c) return notFound("Customer not found.");
    if (!FLAGS[body.flag]) return invalid({ flag: ["Flag the customer Normal, At risk or Critical."] }, "Flag the customer Normal, At risk or Critical.");
    if (openRiskOf(c.id)) return fail(`${c.name} already has churn case ${openRiskOf(c.id).number} open; change its flag there.`, null, 409);
    const r = flagCustomer(c, body);
    return created(riskDetail(r), `${c.name} flagged ${FLAGS[r.flag].name.toLowerCase()}; the CSM, their manager and the account manager are told.`);
  }],
  ["put", "tenant/churn/risks/:id/flag", ({ params, body }) => withRisk(params.id, (r) => {
    if (!FLAGS[body.flag]) return invalid({ flag: ["Flag the customer Normal, At risk or Critical."] }, "Flag the customer Normal, At risk or Critical.");
    if (FLAG_ORDER[body.flag] < FLAG_ORDER[r.flag] && blank(body.reason)) return invalid({ reason: ["Say why the flag is lowered."] }, "Say why the flag is lowered.");
    r.flag = body.flag;
    if (body.reason) r.reason = body.reason;
    return riskAnswer(r, `Flag changed to ${FLAGS[r.flag].name}.`);
  })],
  ["post", "tenant/churn/risks/:id/triggers", ({ params, body }) => withRisk(params.id, (r) => {
    if (blank(body.trigger)) return invalid({ trigger: ["Choose the trigger."] }, "Choose the trigger.");
    r.triggers.push({ id: uid("trg"), trigger: body.trigger, detail: body.detail || null, raises: body.raises || null, source: "manual", detected_at: now(), cleared_at: null });
    if (body.raises && FLAG_ORDER[body.raises] > FLAG_ORDER[r.flag]) r.flag = body.raises;
    return riskAnswer(r, "Trigger captured.");
  })],
  ["post", "tenant/churn/risks/:id/playbook", ({ params, body }) => withRisk(params.id, (r) =>
    (startPlaybook(r, body.playbook) ? riskAnswer(r, "Playbook started: its steps are tasks in the owners' lists.") : invalid({ playbook: ["Choose one of the company's retention playbooks."] }, "Choose one of the company's retention playbooks.")))],
  ["post", "tenant/churn/risks/:id/actions", ({ params, body }) => withRisk(params.id, (r) => {
    if (blank(body.action) || blank(body.due_on)) return invalid({ action: blank(body.action) ? ["Say what is to be done."] : undefined, due_on: blank(body.due_on) ? ["Give the date it is due."] : undefined }, "Say what is to be done and by when.");
    r.actions.push({ id: uid("act"), action: body.action, description: body.description || null, owner_id: body.owner_id || r.owner_id, due_on: body.due_on, status: "open", playbook: null, step: null, source: "manual", done_at: null });
    return riskAnswer(r, "Action added to the retention plan as a task.");
  })],
  ["post", "tenant/churn/actions/:actionId/complete", ({ params }) => {
    const found = findAction(params.actionId);
    if (!found) return notFound("Retention action not found.");
    Object.assign(found.a, { status: "done", done_at: now() });
    return riskAnswer(found.r, "Action done.");
  }],
  ["post", "tenant/churn/risks/:id/saves", ({ params, body }) => withRisk(params.id, (r) => addSave(r.saves, body, { churn_risk_id: r.id }) ?? riskAnswer(r, "Save attempt recorded."))],
  ["put", "tenant/churn/saves/:saveId", ({ params, body }) => {
    const found = findSave(params.saveId);
    if (!found) return notFound("Save attempt not found.");
    ["outcome", "note", "offer", "value_offered"].forEach((k) => k in body && (found.s[k] = body[k]));
    return riskAnswer(found.r, "Save attempt updated.");
  }],
  ["post", "tenant/churn/risks/:id/escalate", ({ params, body }) => withRisk(params.id, (r) => {
    if (r.escalated_at) return fail(`${r.number} was escalated already.`, null, 409);
    escalate(r, body.note);
    return riskAnswer(r, "Escalated to management; they are told in-app and by email.");
  })],
  ["post", "tenant/churn/risks/:id/recover", ({ params, body }) => withRisk(params.id, (r) => {
    if (blank(body.how)) return invalid({ how: ["Say how the customer was kept."] }, "Say how the customer was kept.");
    closeRisk(r, "recovered", body.how);
    recoveryStore.push({ id: uid("rec"), kind: "saved", customer_id: r.customer_id, how: body.how, value_saved: Number(body.value_saved) || r.arr, playbook: r.playbook, recovered_on: today(), risk_id: r.id, exit_id: null, opportunity: null });
    recoverCustomer(r.customer_id);
    return riskAnswer(r, "Recovered: the customer is kept and the save is in the recovery register.");
  })],
  ["post", "tenant/churn/risks/:id/close", ({ params, body }) => withRisk(params.id, (r) => {
    if (blank(body.note)) return invalid({ note: ["Say why the case is closed."] }, "Say why the case is closed.");
    closeRisk(r, "closed", body.note);
    return riskAnswer(r, "Case closed.");
  })],
  ["post", "tenant/churn/risks/:id/churned", ({ params, body }) => withRisk(params.id, (r) => {
    if (blank(body.reason_code)) return invalid({ reason_code: ["Say why the customer is leaving: choose a churn reason."] }, "Say why the customer is leaving: choose a churn reason.");
    closeRisk(r, "churned", body.note || reasonOf(body.reason_code)?.name || body.reason_code);
    r.reason_code = body.reason_code;
    const c = customerOf(r.customer_id);
    let exit = exitStore.find((e) => sameId(e.customer_id, r.customer_id) && isOpen(e)) ?? null;
    if (!exit && body.open_exit !== false) {
      exit = newExit(c, { reason_code: body.reason_code, competitor_id: body.competitor_id, note: body.note, exit_date: body.exit_date, requested_by_name: body.requested_by_name || "Recorded from the churn case" });
      exit.churn_risk_id = r.id;
    }
    return ok({ ...riskDetail(r), exit_id: exit?.id ?? null }, `Churn recorded${exit ? `; exit ${exit.number} is open for review, approval and the exit checklist.` : "."}`);
  })],
  ["post", "tenant/churn/evaluate", ({ body }) => (body.customer_id
    ? ok({ customers: 1, raised: 0, cleared: 0 }, "No churn rule holds for this customer.")
    : ok(null, "The churn rules are running for every customer; new flags arrive as notices.", 202))],

  // Exits
  ["get", "tenant/exits/meta", () => ok({
    stages: Object.entries(STAGES).map(([key, s]) => ({ key, ...s })),
    channels: Object.entries(CHANNELS).map(([key, name]) => ({ key, name })),
    checklist: structuredClone(rules().exit_checklist),
    customers: sampleCustomers.filter((c) => ACTIVE.includes(lifecycle(c)) && !exitStore.some((e) => sameId(e.customer_id, c.id) && isOpen(e))).sort((a, b) => a.name.localeCompare(b.name)).map((c) => ({
      id: c.id, code: c.code, name: c.name, lifecycle: lifecycle(c), contacts: contactsOf(c.id).map((k) => ({ id: k.id, name: k.name, title: k.title })),
      subscriptions: c.product_ids.map((pid) => ({ id: `sub-${c.id}-${pid}`, product: productShort(pid), licences: c.users?.licensed ?? null, status: "active" })),
    })),
    staff: staff.filter((s) => s.status === "active").map((s) => ({ id: s.id, name: s.name })),
    can: { create: true, edit: true, approve: true, close: true, win_back: true },
  })],
  ["get", "tenant/exits", ({ query }) => {
    const all = exitList(query, { stage: false });
    return ok({
      ...resourcePage(exitList(query).map(exitRow), query),
      counts: Object.entries(STAGES).map(([key, s]) => ({ key, name: s.name, count: all.filter((e) => e.stage === key).length })),
      summary: summary(), masked: false, can: { create: true, edit: true, approve: true, close: true, win_back: true },
    });
  }],
  ["get", "tenant/exits/:id", ({ params }) => withExit(params.id, (e) => ok(exitDetail(e)))],
  ["post", "tenant/exits", ({ body }) => {
    const missing = exitMissing(body);
    if (missing) return missing;
    const c = customerOf(body.customer_id);
    if (!c) return notFound("Customer not found.");
    if (exitStore.some((e) => sameId(e.customer_id, c.id) && isOpen(e))) return fail(`${c.name} has an exit open already.`, null, 409);
    const e = newExit(c, body);
    return created(exitDetail(e), `Exit ${e.number} logged; ${c.name} is exiting. Review the account, then send it for approval.`);
  }],
  ["put", "tenant/exits/:id", ({ params, body }) => withExit(params.id, (e) => {
    ["exit_date", "requested_by_name", "contact_id", "channel", "note", "obligations", "contract_terms", "owner_id", "value"].forEach((k) => k in body && (e[k] = body[k]));
    return answer(e, "Saved.");
  })],
  ["post", "tenant/exits/:id/review", ({ params }) => withExit(params.id, (e) => {
    if (e.stage !== "requested") return fail(`${e.number} is past the request.`, null, 409);
    e.review_started_at = now();
    e.obligations ??= "Term ends at the renewal date; 30-day notice required.";
    e.contract_terms ??= "Standard terms.";
    move(e, "review");
    return answer(e, "Account review started.");
  })],
  ["put", "tenant/exits/:id/reason", ({ params, body }) => withExit(params.id, (e) => {
    if (blank(body.reason_code)) return invalid({ reason_code: ["Say why the customer is leaving: choose an exit reason."] }, "Say why the customer is leaving: choose an exit reason.");
    e.audit.push(auditItem(`Reason changed from ${e.reason_code} to ${body.reason_code}`, body.note || null, { kind: "outcome", tone: "warning" }));
    Object.assign(e, { reason_code: body.reason_code, competitor_id: body.competitor_id || null, note: body.note || e.note });
    return answer(e, "Reason updated; the earlier one stays in the history.");
  })],
  ["post", "tenant/exits/:id/invoices", ({ params, body }) => withExit(params.id, (e) => {
    if (blank(body.number) || blank(body.amount)) return invalid({ number: ["Give the invoice number and amount."] }, "Give the invoice number and amount.");
    if (e.invoices.some((i) => i.number === body.number)) return fail(`${body.number} is on this exit already.`, null, 409);
    e.invoices.push({ id: uid("inv"), number: body.number, amount: Number(body.amount), due_on: body.due_on || null, status: body.status || "open", note: body.note || null });
    return answer(e, "Invoice added to the account review.");
  })],
  ["put", "tenant/exits/invoices/:invoiceId", ({ params, body }) => {
    const found = findInvoice(params.invoiceId);
    if (!found) return notFound("Invoice not found.");
    ["number", "amount", "due_on", "status", "note"].forEach((k) => k in body && (found.i[k] = body[k]));
    return answer(found.e, "Invoice updated.");
  }],
  ["delete", "tenant/exits/invoices/:invoiceId", ({ params }) => {
    const found = findInvoice(params.invoiceId);
    if (!found) return notFound("Invoice not found.");
    found.e.invoices.splice(found.e.invoices.indexOf(found.i), 1);
    return answer(found.e, "Invoice removed.");
  }],
  ["post", "tenant/exits/:id/submit", ({ params }) => withExit(params.id, (e) => {
    if (!["requested", "review"].includes(e.stage) || e.approval_status === "pending") return fail(`${e.number} cannot be sent for approval now.`, null, 409);
    e.chain = APPROVERS.map(([step, approver], i) => ({ sequence: i + 1, step, approver, action: "pending", comments: null, acted_at: null, due_at: at(plusDays(2 + i)) }));
    e.approval_status = "pending";
    if (e.stage === "requested") move(e, "review");
    e.audit.push(auditItem("Sent for approval: Customer exit", null, { kind: "approval", tone: "warning", icon: "iconoir-send" }));
    return answer(e, `Sent for approval (${e.reference}).`);
  })],
  ["post", "tenant/exits/:id/approve", ({ params, body }) => withExit(params.id, (e) => {
    const step = waitingOf(e);
    if (!step) return fail("Nothing is waiting for approval on this exit.", null, 409);
    Object.assign(step, { action: "approved", comments: body.comment || null, acted_at: now(), approver: step.approver });
    e.audit.push(auditItem(`${step.step}: Approved`, body.comment || null, { kind: "approval", tone: "success", icon: "iconoir-check-circle" }));
    if (waitingOf(e)) return answer(e, "Approved; passed to the next step.");
    e.approval_status = "approved";
    e.checklist = rules().exit_checklist.map((x) => ({ id: uid("chk"), key: x.key, area: x.area, item: x.item, owner_key: x.owner, owner_id: ownerStaff(x.owner, customerOf(e.customer_id)), required: x.required !== false, done_at: null, done_by: null, note: null }));
    move(e, "approved");
    return answer(e, "Exit approved: the checklist is open and its owners are told.");
  })],
  ["post", "tenant/exits/:id/reject", ({ params, body }) => withExit(params.id, (e) => {
    if (blank(body.comment)) return invalid({ comment: ["Say why the exit is rejected."] }, "Say why the exit is rejected.");
    const step = waitingOf(e);
    if (!step) return fail("Nothing is waiting for approval on this exit.", null, 409);
    Object.assign(step, { action: "rejected", comments: body.comment, acted_at: now() });
    Object.assign(e, { approval_status: "rejected", decision_note: body.comment });
    e.audit.push(auditItem(`${step.step}: Rejected`, body.comment, { kind: "approval", tone: "danger", icon: "iconoir-xmark-circle" }));
    return answer(e, "Rejected; whoever logged it is told.");
  })],
  ["post", "tenant/exits/checklist/:itemId", ({ params, body }) => {
    const found = findItem(params.itemId);
    if (!found) return notFound("Checklist item not found.");
    const { e, x } = found;
    if (!["approved", "checklist"].includes(e.stage)) return fail("The checklist opens once the exit is approved.", null, 409);
    const done = [true, "true", 1, "1"].includes(body.done);
    if (done && x.key === "payment" && outstanding(e) > 0) return fail("Waiting for the outstanding invoices to be settled or written off.", null, 409);
    Object.assign(x, { done_at: done ? now() : null, done_by: done ? ME : null, note: body.note ?? x.note });
    if (done) e.audit.push(auditItem(`Checklist: ${x.item}`, null, { kind: "task", tone: "success", icon: "iconoir-check" }));
    if (e.stage === "approved") move(e, "checklist");
    return answer(e, done ? "Ticked." : "Unticked.");
  }],
  ["post", "tenant/exits/:id/deactivate", ({ params }) => withExit(params.id, (e) => {
    if (!["approved", "checklist"].includes(e.stage)) return fail(`${e.number} is ${STAGES[e.stage].name.toLowerCase()}; an approved exit with its checklist done is deactivated.`, null, 409);
    const open = e.checklist.filter((x) => x.required && !x.done_at);
    if (open.length) return fail(`Complete the exit checklist first: ${open.map((x) => x.item).join("; ")}.`, { details: { missing: open.map((x) => x.item) } }, 422);
    const preview = exitDetail(e).deactivation;
    e.deactivation = { contracts: preview.contracts, subscriptions: 1, product_accounts: preview.product_accounts, connections: [], renewals: [], users: `${preview.users ?? "The"} customer user(s) lose access with the product accounts closed and the connections revoked.` };
    e.deactivated_at = now();
    move(e, "deactivated");
    return answer(e, `Account deactivated: ${e.deactivation.contracts.length} contract(s) terminated, ${e.deactivation.product_accounts.length} product account(s) closed, 0 connection(s) revoked.`);
  })],
  ["post", "tenant/exits/:id/confirm", ({ params, body }) => withExit(params.id, (e) => {
    if (e.stage !== "deactivated") return fail(`${e.number} is confirmed once the account is deactivated.`, null, 409);
    const recyclable = reasonRef(e.reason_code).recyclable;
    Object.assign(e, { final_date: body.final_date || e.exit_date, outstanding_issues: body.outstanding_issues || null, confirmed_at: now() });
    e.reengage_on = body.reengage_on || (recyclable ? plusDays(Number(rules().win_back.reengage_days ?? 180), e.final_date) : null);
    move(e, "confirmed");
    moveCustomerLifecycle(e.customer_id, "former", "exit_confirmed", `Exit ${e.number}`);
    const risk = riskStore.find((r) => sameId(r.id, e.churn_risk_id) && r.status === "open");
    if (risk) Object.assign(risk, { status: "churned", closed_at: now(), close_note: `Exit ${e.number} confirmed`, reason_code: e.reason_code });
    return answer(e, `Exit confirmed: ${customerOf(e.customer_id)?.name} is a former customer${e.reengage_on ? `; re-engagement on ${e.reengage_on}.` : "."}`);
  })],
  ["post", "tenant/exits/:id/withdraw", ({ params, body }) => withExit(params.id, (e) => {
    if (blank(body.reason)) return invalid({ reason: ["Say why the customer is staying."] }, "Say why the customer is staying.");
    if (!["requested", "review", "approved", "checklist"].includes(e.stage)) return fail(`${e.number} can no longer be withdrawn.`, null, 409);
    Object.assign(e, { withdrawn_at: now(), withdraw_reason: body.reason, approval_status: e.approval_status === "pending" ? "cancelled" : e.approval_status });
    e.chain.filter((a) => a.action === "pending").forEach((a) => (a.action = "cancelled"));
    move(e, "withdrawn", "Withdrawn: the customer stays", body.reason);
    recoveryStore.push({ id: uid("rec"), kind: "withdrawn", customer_id: e.customer_id, how: body.reason, value_saved: Number(body.value_saved) || e.value, playbook: null, recovered_on: today(), risk_id: e.churn_risk_id, exit_id: e.id, opportunity: null });
    recoverCustomer(e.customer_id);
    return answer(e, "Withdrawn: the customer stays, and the save is in the recovery register.");
  })],
  ["post", "tenant/exits/:id/letter", ({ params, body }) => withExit(params.id, (e) => {
    e.letter = { id: uid("att"), name: body.letter?.name ?? "Exit letter.pdf" };
    return answer(e, "Letter attached.");
  })],
  ["put", "tenant/exits/:id/reengagement", ({ params, body }) => withExit(params.id, (e) => {
    if (blank(body.reengage_on) || daysTo(body.reengage_on) < 0) return invalid({ reengage_on: ["Choose a date from today on."] }, "Choose a date from today on.");
    e.reengage_on = body.reengage_on;
    return answer(e, "Re-engagement date set; the owner has a task for it.");
  })],
  ["post", "tenant/exits/:id/survey", ({ params }) => withExit(params.id, (e) => {
    const contact = contactsOf(e.customer_id).find((k) => sameId(k.id, e.contact_id)) ?? contactsOf(e.customer_id)[0];
    e.survey.invitation = { id: uid("sur"), status: "scheduled", channel: "email", contact: contact?.name ?? null, send_at: now(), sent_at: null, survey_id: 1 };
    return answer(e, `Exit survey scheduled (${contact?.name ?? "the primary contact"}).`);
  })],
  ["post", "tenant/exits/:id/saves", ({ params, body }) => withExit(params.id, (e) => addSave(e.saves, body, { customer_exit_id: e.id }) ?? answer(e, "Save attempt recorded."))],
  ["get", "tenant/records/customer_exit/:id/attachments/:attachmentId/download", ({ params }) => ok({ url: "#", filename: findExit(params.id)?.letter?.name ?? "letter", expires_in: 300 })],

  // Former customers and win-back
  ["get", "tenant/win-back", ({ query }) => {
    const all = former();
    const list = ["1", "true", 1, true].includes(query.due) ? all.filter((f) => f.due) : all;
    const opps = pipelineOpps();
    const open = opps.filter((o) => !o.closed_at);
    const won = wonBack();
    return ok({
      former: list,
      pipeline: {
        stages: pipelineStages.map((s) => ({ key: s.key, name: s.name, kind: s.kind, count: opps.filter((o) => o.stage === s.key).length, value: sum(opps.filter((o) => o.stage === s.key), (o) => o.value) })),
        opportunities: opps.map((o) => ({ id: o.id, code: o.code, name: o.name, stage: o.stage, value: o.value, customer: oppCustomer(o), owner: staffRow(o.owner_id), is_open: !o.closed_at,
          opened_on: String(o.created_at).slice(0, 10), closed_at: o.closed_at ? String(o.closed_at).slice(0, 10) : null, won_on: o.won_on })),
      },
      recovered: won.map(({ kind, ...x }) => x),
      totals: { former: list.length, former_value: sum(list, (f) => f.last_value), due: list.filter((f) => f.due).length, open: open.length, open_value: sum(open, (o) => o.value), won_back: won.length,
        rate: list.length + won.length ? Math.round((1000 * won.length) / (list.length + won.length)) / 10 : null },
      masked: false, can: { create: true },
    });
  }],
  ["post", "tenant/win-back", ({ body }) => {
    const c = customerOf(body.customer_id);
    if (!c) return notFound("Customer not found.");
    if (lifecycle(c) !== "former") return fail(`${c.name} is not a former customer; a win-back is for a former customer.`, null, 409);
    const openOpp = pipelineOpps().find((o) => sameId(o.customer_id, c.id) && !o.closed_at);
    if (openOpp) return fail(`${c.name} already has win-back ${openOpp.code} open.`, null, 409);
    const opp = createWinBack(c, body);
    return created({ id: opp.id, code: opp.code, stage: opp.stage, kind: "win_back" }, `${opp.code} opened to win ${c.name} back; it runs in the opportunity pipeline.`);
  }],

  // Churn analysis
  ["get", "tenant/reports/churn", () => {
    const rows = analysisRows();
    return ok({ period: period(), masked: false, heuristic: true, ...Object.fromEntries(Object.entries(sections).map(([k, f]) => [k.replace("-", "_"), f(rows)])) });
  }],
  ["get", "tenant/reports/churn/:section", ({ params }) => (sections[params.section]
    ? ok({ period: period(), masked: false, heuristic: true, [params.section.replace("-", "_")]: sections[params.section](analysisRows()) })
    : notFound("There is no such churn report."))],
];
